import type {
  ScenarioResponseApi,
  ScenarioShareResponseApi,
  ScenarioChangeResponseApi,
  ScenarioCommentResponseApi,
  ScenarioVersionResponseApi,
  ScenarioDependencyResponseApi,
  ScenarioApprovalResponseApi,
  CreateScenarioRequestApi,
  CreateScenarioChangeRequestApi,
  UpdateScenarioChangeRequestApi,
} from '../models/scenario';
import type {
  ComprehensiveScenario,
  ScenarioApproval,
  ScenarioChange,
  ScenarioComment,
  ScenarioShare,
  ScenarioVersion,
  ScenarioDependency,
  ScenarioStatus,
  ScenarioPriority,
  ScenarioLabel,
  ChangeType,
  DependencyType,
  ScenarioPermission,
  ApprovalStatus,
} from '../../types/scenarios';

/**
 * Map a backend ScenarioShareResponse to the frontend ScenarioShare type.
 */
export function mapShareFromApi(share: ScenarioShareResponseApi): ScenarioShare {
  return {
    id: String(share.id),
    userId: share.userId,
    userName: share.userName,
    permission: share.permission as ScenarioPermission,
    sharedAt: new Date(share.dateCreated),
    sharedBy: share.sharedByUserName,
  };
}

/**
 * Map a backend ScenarioChangeResponse to the frontend ScenarioChange type.
 *
 * Key transformations:
 * - id: number -> string
 * - changeType -> type (field rename)
 * - details: JSON string -> Record<string, unknown>
 * - Engine typed columns (monthlyAmount, contractValue, isReduction) are merged INTO
 *   the details bag so the existing scenarioEngine.ts can read them without modification.
 * - Date strings -> Date objects
 */
export function mapChangeFromApi(change: ScenarioChangeResponseApi): ScenarioChange {
  let details: Record<string, unknown> = {};
  if (change.details) {
    try {
      details = JSON.parse(change.details);
    } catch {
      details = {};
    }
  }

  // Merge engine typed columns into details for backward compatibility with scenarioEngine.ts
  if (change.monthlyAmount != null) {
    const ct = change.changeType;
    if (['change-costs', 'adjust-cost', 'add-cost'].includes(ct)) {
      if (change.isReduction) {
        details.monthlySavings = change.monthlyAmount;
      } else {
        details.monthlyIncrease = change.monthlyAmount;
      }
    } else if (['add-revenue', 'adjust-revenue', 'change-revenue'].includes(ct)) {
      details.monthlyAmount = change.monthlyAmount;
    } else if (ct === 'win-customer' || ct === 'lose-customer') {
      details.monthlyValue = change.monthlyAmount;
    } else if (ct === 'hire-employees') {
      details.monthlyCost = change.monthlyAmount;
    } else {
      details.monthlyAmount = change.monthlyAmount;
    }
  }
  if (change.contractValue != null) {
    details.contractValue = change.contractValue;
  }
  if (change.isReduction != null) {
    details.changeType = change.isReduction ? 'reduction' : 'increase';
  }

  return {
    id: String(change.id),
    type: change.changeType as ChangeType,
    name: change.name,
    description: change.description ?? '',
    amount: change.amount,
    date: new Date(change.date),
    effectiveDate: change.effectiveDate ? new Date(change.effectiveDate) : undefined,
    endDate: change.endDate ? new Date(change.endDate) : undefined,
    details,
    createdAt: new Date(change.dateCreated),
    createdBy: change.createdByUserName,
    supplier: change.supplier ?? undefined,
    originalDueDate: change.originalDueDate ? new Date(change.originalDueDate) : undefined,
    newDueDate: change.newDueDate ? new Date(change.newDueDate) : undefined,
    delayDays: change.delayDays ?? undefined,
  };
}

/**
 * Map a backend ScenarioCommentResponse to the frontend ScenarioComment type.
 */
export function mapCommentFromApi(comment: ScenarioCommentResponseApi): ScenarioComment {
  return {
    id: String(comment.id),
    userId: comment.createdByUserId,
    authorName: comment.authorName,
    userRole: comment.userRole ?? '',
    text: comment.text,
    createdAt: new Date(comment.dateCreated),
  };
}

/**
 * Map a backend ScenarioResponse + its changes and comments to the frontend ComprehensiveScenario type.
 *
 * Fields not yet returned by the backend (tags, collections, etc.)
 * are defaulted to empty values.
 */
/**
 * Map a backend ScenarioVersionResponse to the frontend ScenarioVersion type.
 */
export function mapVersionFromApi(version: ScenarioVersionResponseApi): ScenarioVersion {
  let changes: ScenarioChange[] = [];
  if (version.changesSnapshot) {
    try {
      const rawChanges = JSON.parse(version.changesSnapshot) as ScenarioChangeResponseApi[];
      changes = rawChanges.map(mapChangeFromApi);
    } catch {
      /* empty */
    }
  }

  let scenarioData: Record<string, unknown> = {};
  if (version.scenarioSnapshot) {
    try {
      scenarioData = JSON.parse(version.scenarioSnapshot);
    } catch {
      /* empty */
    }
  }

  return {
    id: String(version.id),
    version: version.versionNumber,
    description: version.description,
    createdBy: version.createdByUserId,
    createdByName: version.createdByUserName,
    createdAt: new Date(version.dateCreated),
    data: {
      ...scenarioData,
      changes,
    } as Partial<ComprehensiveScenario>,
  };
}

/**
 * Map a backend ScenarioDependencyResponse to the frontend ScenarioDependency type.
 */
export function mapDependencyFromApi(dep: ScenarioDependencyResponseApi): ScenarioDependency {
  return {
    id: String(dep.id),
    type: dep.dependencyType as DependencyType,
    targetScenarioId: String(dep.targetScenarioId),
    isMet: dep.isMet,
    impact: dep.impact ?? undefined,
  };
}

/**
 * Map a backend ScenarioApprovalResponse to the frontend ScenarioApproval type.
 */
export function mapApprovalFromApi(approval: ScenarioApprovalResponseApi): ScenarioApproval {
  return {
    id: String(approval.id),
    userId: approval.userId,
    approverId: approval.userId,
    userName: approval.userName,
    userRole: approval.stageName,
    status: approval.decision as ApprovalStatus,
    comments: approval.comments ?? undefined,
    timestamp: new Date(approval.dateCreated),
    order: approval.stageOrder,
  };
}

export function mapScenarioFromApi(
  scenario: ScenarioResponseApi,
  changes: ScenarioChangeResponseApi[] = [],
  comments: ScenarioCommentResponseApi[] = [],
): ComprehensiveScenario {
  return {
    id: String(scenario.id),
    name: scenario.name,
    description: scenario.description ?? '',
    status: scenario.status as ScenarioStatus,
    priority: scenario.priority as ScenarioPriority,
    label: (scenario.label as ScenarioLabel) ?? null,
    category: scenario.category as ComprehensiveScenario['category'],
    createdBy: scenario.createdByUserId,
    createdByName: scenario.createdByUserName,
    createdAt: new Date(scenario.dateCreated),
    modifiedAt: new Date(scenario.dateModified),
    modifiedBy: scenario.modifiedByUserName ?? scenario.modifiedByUserId,
    shares: (scenario.shares ?? []).map(mapShareFromApi),
    isFavorite: scenario.isFavorite,
    changes: changes.map(mapChangeFromApi),
    dependencies: (scenario.dependencies ?? []).map(mapDependencyFromApi),
    comments: comments.map(mapCommentFromApi),
    commentCount: scenario.commentCount,
    versions: [],
    currentVersion: scenario.currentVersion,
    parkedAt: scenario.parkedAt ? new Date(scenario.parkedAt) : undefined,
    parkedBy: scenario.parkedByName ?? scenario.parkedBy ?? undefined,
    parkedReason: scenario.parkedReason ?? undefined,
    reviewDate: scenario.reviewDate ? new Date(scenario.reviewDate) : undefined,
    reviewedAt: scenario.reviewedAt ? new Date(scenario.reviewedAt) : undefined,
    reviewedByUserId: scenario.reviewedByUserId ?? undefined,
    approvalWorkflowId: scenario.approvalWorkflowId ?? null,
    currentStageId: scenario.currentStageId ?? null,
    currentStageOrder: scenario.currentStageOrder ?? null,
    currentStageName: scenario.currentStageName ?? null,
    submittedByUserId: scenario.submittedByUserId ?? null,
    submittedAt: scenario.submittedAt ? new Date(scenario.submittedAt) : null,
    isFinalStage: scenario.isFinalStage ?? undefined,
    approvalHistory: (scenario.approvals ?? []).map(mapApprovalFromApi),
    collectionIds: [],
    metrics: {
      minCash: 0,
      minCashDate: new Date(),
      runway: 0,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: 0,
      costChangeAmount: 0,
      netImpact: 0,
      endingCash: 0,
    },
    tags: [],
    startingPoint: (scenario.startingPoint as 'base' | 'blank') ?? 'base',
    currentUserPermission: (scenario.currentUserPermission as ComprehensiveScenario['currentUserPermission']) ?? null,
  };
}

/**
 * Map a frontend ComprehensiveScenario to a backend CreateScenarioRequest.
 */
export function toCreateScenarioRequest(
  scenario: Omit<ComprehensiveScenario, 'id' | 'createdAt' | 'modifiedAt'>,
): CreateScenarioRequestApi {
  return {
    name: scenario.name,
    description: scenario.description || null,
    status: scenario.status,
    priority: scenario.priority,
    label: scenario.label || null,
    category: scenario.category || null,
    startingPoint: scenario.startingPoint || 'base',
  };
}

/**
 * Map a frontend ScenarioChange to a backend CreateScenarioChangeRequest.
 *
 * Extracts engine fields (monthlyAmount, contractValue, isReduction) from the
 * details bag and puts them into the typed request fields.
 */
export function toCreateChangeRequest(
  change: Omit<ScenarioChange, 'id' | 'createdAt' | 'createdBy'>,
): CreateScenarioChangeRequestApi {
  const details = { ...change.details };

  let monthlyAmount: number | null = null;
  let contractValue: number | null = null;
  let isReduction: boolean | null = null;

  const monthlyKeys = [
    'monthlySavings',
    'monthlySaving',
    'monthlyIncrease',
    'monthlyCost',
    'monthlyPayroll',
    'monthlyAmount',
    'monthlyRevenue',
    'monthlyValue',
    'monthlyRevenueLost',
  ];
  for (const key of monthlyKeys) {
    if (details[key] != null && typeof details[key] === 'number') {
      monthlyAmount = details[key] as number;
      delete details[key];
      break;
    }
  }

  const contractKeys = ['contractValue', 'totalValue'];
  for (const key of contractKeys) {
    if (details[key] != null && typeof details[key] === 'number') {
      contractValue = details[key] as number;
      delete details[key];
      break;
    }
  }

  if (details.changeType === 'reduction') {
    isReduction = true;
    delete details.changeType;
  } else if (details.changeType === 'increase') {
    isReduction = false;
    delete details.changeType;
  }

  const detailsJson = Object.keys(details).length > 0 ? JSON.stringify(details) : null;

  return {
    changeType: change.type,
    name: change.name,
    description: change.description || null,
    amount: change.amount,
    date: change.date instanceof Date ? change.date.toISOString() : (change.date as unknown as string),
    effectiveDate: change.effectiveDate instanceof Date
      ? change.effectiveDate.toISOString()
      : (change.effectiveDate as unknown as string ?? null),
    endDate: change.endDate instanceof Date
      ? change.endDate.toISOString()
      : (change.endDate as unknown as string ?? null),
    monthlyAmount,
    contractValue,
    isReduction,
    details: detailsJson,
    supplier: change.supplier ?? null,
    originalDueDate: change.originalDueDate instanceof Date
      ? change.originalDueDate.toISOString()
      : (change.originalDueDate as unknown as string ?? null),
    newDueDate: change.newDueDate instanceof Date
      ? change.newDueDate.toISOString()
      : (change.newDueDate as unknown as string ?? null),
    delayDays: change.delayDays ?? null,
  };
}

/**
 * Map partial ScenarioChange updates to an UpdateScenarioChangeRequest.
 */
export function toUpdateChangeRequest(
  updates: Partial<ScenarioChange>,
): UpdateScenarioChangeRequestApi {
  const request: UpdateScenarioChangeRequestApi = {};

  if (updates.type !== undefined) request.changeType = updates.type;
  if (updates.name !== undefined) request.name = updates.name;
  if (updates.description !== undefined) request.description = updates.description || null;
  if (updates.amount !== undefined) request.amount = updates.amount;
  if (updates.date !== undefined) {
    request.date = updates.date instanceof Date
      ? updates.date.toISOString()
      : (updates.date as unknown as string);
  }
  if (updates.effectiveDate !== undefined) {
    request.effectiveDate = updates.effectiveDate instanceof Date
      ? updates.effectiveDate.toISOString()
      : (updates.effectiveDate as unknown as string ?? null);
  }
  if (updates.endDate !== undefined) {
    request.endDate = updates.endDate instanceof Date
      ? updates.endDate.toISOString()
      : (updates.endDate as unknown as string ?? null);
  }
  if (updates.supplier !== undefined) request.supplier = updates.supplier ?? null;
  if (updates.delayDays !== undefined) request.delayDays = updates.delayDays ?? null;
  if (updates.originalDueDate !== undefined) {
    request.originalDueDate = updates.originalDueDate instanceof Date
      ? updates.originalDueDate.toISOString()
      : (updates.originalDueDate as unknown as string ?? null);
  }
  if (updates.newDueDate !== undefined) {
    request.newDueDate = updates.newDueDate instanceof Date
      ? updates.newDueDate.toISOString()
      : (updates.newDueDate as unknown as string ?? null);
  }

  if (updates.details !== undefined) {
    const details = { ...updates.details };
    let monthlyAmount: number | null = null;
    let contractValue: number | null = null;
    let isReduction: boolean | null = null;

    const monthlyKeys = [
      'monthlySavings',
      'monthlySaving',
      'monthlyIncrease',
      'monthlyCost',
      'monthlyPayroll',
      'monthlyAmount',
      'monthlyRevenue',
      'monthlyValue',
      'monthlyRevenueLost',
    ];
    for (const key of monthlyKeys) {
      if (details[key] != null && typeof details[key] === 'number') {
        monthlyAmount = details[key] as number;
        delete details[key];
        break;
      }
    }
    const contractKeys = ['contractValue', 'totalValue'];
    for (const key of contractKeys) {
      if (details[key] != null && typeof details[key] === 'number') {
        contractValue = details[key] as number;
        delete details[key];
        break;
      }
    }
    if (details.changeType === 'reduction') {
      isReduction = true;
      delete details.changeType;
    } else if (details.changeType === 'increase') {
      isReduction = false;
      delete details.changeType;
    }

    request.monthlyAmount = monthlyAmount;
    request.contractValue = contractValue;
    request.isReduction = isReduction;
    request.details = Object.keys(details).length > 0 ? JSON.stringify(details) : null;
  }

  return request;
}
